import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  Wrench,
  Truck,
  ArrowRight,
  ArrowLeft,
  Play,
  CheckCircle,
  Clock,
  DollarSign,
  BarChart3,
  Shield,
  Zap,
  Users,
  Package,
  FileText,
  MapPin,
  Phone,
  Mail,
  Target,
  Sparkles,
  X,
  MessageCircle,
  Send,
  Bell,
  MousePointer,
  Eye,
  QrCode,
  Droplets,
  FlaskConical,
  Lock,
  Fingerprint,
  AlertTriangle,
  Gavel,
  Calendar,
  Wallet,
  Receipt,
  Settings,
  Activity,
  HardHat,
  Plug,
  Wind,
  ScanLine,
  History,
  ClipboardCheck,
  Award,
  Layers
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { ShareButtons } from "@/components/ShareButtons";

// Digital Isaac Assets
const DIGITAL_ISAAC = "https://static.prod-images.emergentagent.com/jobs/0bc119bf-ae15-43e9-9f13-14b53e074c94/images/fe24d5e280d09d6a7d0c6d06f6b149605eaa9a0fa1be0b67e18ee25456bf0b6f.png";
const DIGITAL_ISAAC_VIDEO = "https://customer-assets.emergentagent.com/job_hospital-mgmt-27/artifacts/r8ou0x9f_freepik_animate-image-as-a-digital-concierge-for-hospital-_kling_1080p_16-9_24fps_97399.mp4";

// Demo Screenshot URLs - Using stock healthcare images that simulate the interface
const DEMO_SCREENSHOTS = {
  hospitalDashboard: "https://images.unsplash.com/photo-1516549655169-df83a0774514?w=1200&h=800&fit=crop",
  maintenancePipeline: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1200&h=800&fit=crop",
  backorderTracking: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=1200&h=800&fit=crop",
  quickbids: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&h=800&fit=crop",
  equipmentRegistry: "https://images.unsplash.com/photo-1581595220892-b0739db3ba8c?w=1200&h=800&fit=crop",
  waterTesting: "https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=1200&h=800&fit=crop",
  gpsTracking: "https://images.unsplash.com/photo-1557853197-aefb550b6fdc?w=1200&h=800&fit=crop",
  contractorEarnings: "https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=1200&h=800&fit=crop",
  supplierQuotes: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200&h=800&fit=crop",
  adminCommand: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=800&fit=crop",
  security: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=1200&h=800&fit=crop",
  telegram: "https://images.unsplash.com/photo-1611746872915-64382b5c76da?w=1200&h=800&fit=crop",
  auditTrail: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=800&fit=crop"
};

// Annotation Component - Renders circles and callouts on screenshots
const Annotation = ({ x, y, number, label, color = "cyan" }) => {
  const colorClasses = {
    cyan: "bg-cyan-500 border-cyan-300",
    emerald: "bg-emerald-500 border-emerald-300",
    amber: "bg-amber-500 border-amber-300",
    red: "bg-red-500 border-red-300",
    violet: "bg-violet-500 border-violet-300"
  };
  
  return (
    <div 
      className="absolute transform -translate-x-1/2 -translate-y-1/2 z-10"
      style={{ left: `${x}%`, top: `${y}%` }}
    >
      {/* Pulsing circle */}
      <div className={`relative w-10 h-10 ${colorClasses[color]} rounded-full border-2 flex items-center justify-center animate-pulse`}>
        <span className="text-white font-bold text-sm">{number}</span>
      </div>
      {/* Label */}
      {label && (
        <div className="absolute top-12 left-1/2 -translate-x-1/2 bg-slate-900/90 px-3 py-1.5 rounded-lg whitespace-nowrap">
          <p className="text-white text-xs font-medium">{label}</p>
        </div>
      )}
    </div>
  );
};

// Animated Arrow Component
const AnimatedArrow = ({ x1, y1, x2, y2, color = "cyan" }) => {
  const colorClass = color === "cyan" ? "stroke-cyan-400" : color === "emerald" ? "stroke-emerald-400" : "stroke-amber-400";
  
  return (
    <svg 
      className="absolute inset-0 w-full h-full pointer-events-none z-5"
      style={{ overflow: 'visible' }}
    >
      <defs>
        <marker
          id={`arrowhead-${color}`}
          markerWidth="10"
          markerHeight="7"
          refX="9"
          refY="3.5"
          orient="auto"
        >
          <polygon
            points="0 0, 10 3.5, 0 7"
            className={color === "cyan" ? "fill-cyan-400" : color === "emerald" ? "fill-emerald-400" : "fill-amber-400"}
          />
        </marker>
      </defs>
      <line
        x1={`${x1}%`}
        y1={`${y1}%`}
        x2={`${x2}%`}
        y2={`${y2}%`}
        className={`${colorClass} stroke-2`}
        strokeDasharray="8,4"
        markerEnd={`url(#arrowhead-${color})`}
      >
        <animate
          attributeName="stroke-dashoffset"
          from="24"
          to="0"
          dur="1s"
          repeatCount="indefinite"
        />
      </line>
    </svg>
  );
};

// Complete 17-Slide Demo Data
const ENHANCED_DEMO_SLIDES = [
  // ========== SLIDE 1: Welcome ==========
  {
    id: "welcome",
    type: "welcome",
    title: "Welcome to AskIsaac",
    subtitle: "The Complete Healthcare Operations Platform",
    description: "One platform connecting hospitals, contractors, and suppliers. From emergency supplies to scheduled maintenance — everything tracked, verified, and compliant.",
    highlights: [
      { icon: Building2, text: "Hospital Command Center" },
      { icon: Wrench, text: "Contractor Network" },
      { icon: Truck, text: "Supply Chain" },
      { icon: Shield, text: "HIPAA Compliant" }
    ]
  },

  // ========== SLIDE 2: Services Overview ==========
  {
    id: "services-overview",
    type: "services",
    title: "Everything Under One Roof",
    subtitle: "From First Request to Final Delivery — We Handle It All",
    services: [
      {
        icon: Droplets,
        name: "Healthcare Water Purification",
        description: "RO systems, DI water, softeners for Sterile Processing & Dialysis",
        color: "cyan"
      },
      {
        icon: Wrench,
        name: "Licensed Plumbing",
        description: "Healthcare-experienced, licensed & insured professionals",
        color: "blue"
      },
      {
        icon: Plug,
        name: "Electrical Services",
        description: "Critical environment certified electricians",
        color: "amber"
      },
      {
        icon: Wind,
        name: "HVAC Services",
        description: "Infection control compliant, healthcare facility rated",
        color: "emerald"
      },
      {
        icon: FlaskConical,
        name: "Water Sampling & Testing",
        description: "AAMI ST108 compliant sampling with lab coordination",
        color: "violet"
      },
      {
        icon: HardHat,
        name: "General Contractor",
        description: "Containment, renovations, compliance-first construction",
        color: "orange"
      }
    ]
  },

  // ========== SLIDE 3: Hospital Dashboard ==========
  {
    id: "hospital-dashboard",
    type: "screenshot",
    title: "Hospital Command Center",
    subtitle: "Your Entire Operation at a Glance",
    screenshotUrl: DEMO_SCREENSHOTS.hospitalDashboard,
    mockInterface: "hospital-dashboard",
    annotations: [
      { x: 10, y: 30, number: 1, label: "Quick Navigation", color: "cyan" },
      { x: 50, y: 15, number: 2, label: "Contextual Banner", color: "cyan" },
      { x: 85, y: 25, number: 3, label: "New Request", color: "emerald" },
      { x: 30, y: 60, number: 4, label: "Live Stats", color: "amber" }
    ],
    benefits: [
      "All pending requests in one view",
      "Real-time status tracking",
      "One-click request submission",
      "Department filtering"
    ]
  },

  // ========== SLIDE 4: Maintenance Pipeline ==========
  {
    id: "maintenance-pipeline",
    type: "screenshot",
    title: "Drag & Drop Maintenance Pipeline",
    subtitle: "Visual Kanban Workflow Management",
    screenshotUrl: DEMO_SCREENSHOTS.maintenancePipeline,
    mockInterface: "kanban",
    annotations: [
      { x: 12, y: 45, number: 1, label: "Intake", color: "cyan" },
      { x: 30, y: 45, number: 2, label: "Triage", color: "amber" },
      { x: 50, y: 45, number: 3, label: "Dispatched", color: "emerald" },
      { x: 70, y: 45, number: 4, label: "In Progress", color: "emerald" },
      { x: 88, y: 45, number: 5, label: "Completed", color: "cyan" }
    ],
    arrows: [
      { x1: 18, y1: 45, x2: 25, y2: 45, color: "cyan" },
      { x1: 36, y1: 45, x2: 44, y2: 45, color: "amber" },
      { x1: 56, y1: 45, x2: 64, y2: 45, color: "emerald" }
    ],
    benefits: [
      "Drag tickets between stages",
      "Priority color coding (HIGH/MED/LOW)",
      "Click for full ticket details",
      "Filter by department or technician"
    ]
  },

  // ========== SLIDE 5: Backorder Tracking ==========
  {
    id: "backorder-tracking",
    type: "screenshot",
    title: "Live Backorder Tracking",
    subtitle: "Never Lose Sight of Critical Supplies",
    screenshotUrl: DEMO_SCREENSHOTS.backorderTracking,
    mockInterface: "backorders",
    annotations: [
      { x: 15, y: 35, number: 1, label: "GPO Alert", color: "red" },
      { x: 45, y: 50, number: 2, label: "Supplier Bids", color: "emerald" },
      { x: 75, y: 35, number: 3, label: "ETA Tracking", color: "cyan" },
      { x: 90, y: 60, number: 4, label: "Edit & Audit", color: "amber" }
    ],
    benefits: [
      "Real-time GPO backorder alerts",
      "Anonymous competitive bidding",
      "First compliant responder wins",
      "Complete audit trail on every edit"
    ]
  },

  // ========== SLIDE 6: QuickBids ==========
  {
    id: "quickbids",
    type: "screenshot",
    title: "QuickBids — Hospital-Initiated Bidding",
    subtitle: "Create Your Own Competitive Bids",
    screenshotUrl: DEMO_SCREENSHOTS.quickbids,
    mockInterface: "quickbids",
    annotations: [
      { x: 20, y: 30, number: 1, label: "Create New Bid", color: "emerald" },
      { x: 50, y: 45, number: 2, label: "Set Requirements", color: "cyan" },
      { x: 80, y: 30, number: 3, label: "Review Responses", color: "amber" },
      { x: 65, y: 65, number: 4, label: "Award Winner", color: "emerald" }
    ],
    benefits: [
      "Post custom supply requests",
      "Set urgency tier (STAT/Priority/Standard)",
      "Compare anonymous supplier bids",
      "Award with one click"
    ]
  },

  // ========== SLIDE 7: Equipment Registry ==========
  {
    id: "equipment-registry",
    type: "screenshot",
    title: "Equipment Registry & QR Codes",
    subtitle: "Scan → View History → Schedule Service",
    screenshotUrl: DEMO_SCREENSHOTS.equipmentRegistry,
    mockInterface: "equipment",
    annotations: [
      { x: 20, y: 35, number: 1, label: "QR Code Scan", color: "cyan" },
      { x: 50, y: 45, number: 2, label: "Asset Details", color: "emerald" },
      { x: 80, y: 35, number: 3, label: "Service History", color: "amber" },
      { x: 65, y: 70, number: 4, label: "Schedule PM", color: "violet" }
    ],
    benefits: [
      "QR code on every asset",
      "Instant mobile lookup",
      "Complete service history",
      "Automated PM scheduling"
    ]
  },

  // ========== SLIDE 8: Water Testing ==========
  {
    id: "water-testing",
    type: "screenshot",
    title: "Water Testing & AAMI ST108 Compliance",
    subtitle: "Critical for Sterile Processing & Dialysis",
    screenshotUrl: DEMO_SCREENSHOTS.waterTesting,
    mockInterface: "water-testing",
    annotations: [
      { x: 20, y: 35, number: 1, label: "Schedule Test", color: "cyan" },
      { x: 50, y: 45, number: 2, label: "Lab Coordination", color: "emerald" },
      { x: 80, y: 35, number: 3, label: "Results & Trends", color: "amber" }
    ],
    benefits: [
      "AAMI ST108 compliant sampling",
      "Automated lab coordination",
      "Results interpretation",
      "Compliance documentation"
    ]
  },

  // ========== SLIDE 9: GPS Contractor Tracking ==========
  {
    id: "gps-tracking",
    type: "screenshot",
    title: "GPS Check-In Verification",
    subtitle: "Know When Contractors Actually Arrive",
    screenshotUrl: DEMO_SCREENSHOTS.gpsTracking,
    mockInterface: "gps",
    annotations: [
      { x: 25, y: 40, number: 1, label: "Live Location", color: "emerald" },
      { x: 60, y: 30, number: 2, label: "Check-In Time", color: "cyan" },
      { x: 75, y: 60, number: 3, label: "Hospital Alert", color: "amber" }
    ],
    benefits: [
      "Live GPS tracking on arrival",
      "Automated check-in/check-out",
      "Instant hospital notifications",
      "Geofence verification"
    ]
  },

  // ========== SLIDE 10: Contractor Earnings ==========
  {
    id: "contractor-earnings",
    type: "screenshot",
    title: "Contractor Earnings & Invoicing",
    subtitle: "Transparent Pay, Streamlined Billing",
    screenshotUrl: DEMO_SCREENSHOTS.contractorEarnings,
    mockInterface: "earnings",
    annotations: [
      { x: 20, y: 35, number: 1, label: "Earnings Summary", color: "emerald" },
      { x: 50, y: 50, number: 2, label: "Job History", color: "cyan" },
      { x: 80, y: 35, number: 3, label: "Generate Invoice", color: "amber" }
    ],
    benefits: [
      "Real-time earnings dashboard",
      "Auto-generated invoices",
      "Time tracking integration",
      "Premium pay for STAT jobs"
    ]
  },

  // ========== SLIDE 11: Supplier Quotes ==========
  {
    id: "supplier-quotes",
    type: "screenshot",
    title: "Supplier Quote Submission",
    subtitle: "Anonymous Bidding = Fair Competition",
    screenshotUrl: DEMO_SCREENSHOTS.supplierQuotes,
    mockInterface: "supplier",
    annotations: [
      { x: 20, y: 35, number: 1, label: "RFQ Alert", color: "amber" },
      { x: 50, y: 50, number: 2, label: "Submit Quote", color: "emerald" },
      { x: 80, y: 35, number: 3, label: "Award Status", color: "cyan" }
    ],
    benefits: [
      "Instant RFQ notifications",
      "Anonymous bidding (no favoritism)",
      "Premium margins on STAT orders",
      "Direct hospital access"
    ]
  },

  // ========== SLIDE 12: Admin Command Center ==========
  {
    id: "admin-command",
    type: "screenshot",
    title: "Admin Command Center",
    subtitle: "Orchestrate Everything From One Place",
    screenshotUrl: DEMO_SCREENSHOTS.adminCommand,
    mockInterface: "admin",
    annotations: [
      { x: 15, y: 30, number: 1, label: "Network Overview", color: "cyan" },
      { x: 40, y: 45, number: 2, label: "Pipeline View", color: "emerald" },
      { x: 70, y: 30, number: 3, label: "Dispatch Queue", color: "amber" },
      { x: 85, y: 60, number: 4, label: "Analytics", color: "violet" }
    ],
    benefits: [
      "See all hospitals, contractors, suppliers",
      "Dispatch and reassign jobs",
      "Monitor SLA compliance",
      "Revenue and performance analytics"
    ]
  },

  // ========== SLIDE 13: Security & HIPAA ==========
  {
    id: "security",
    type: "security",
    title: "Hospital-Grade Security Suite",
    subtitle: "Zero-Knowledge Architecture + HIPAA-Ready Compliance",
    features: [
      {
        icon: Shield,
        title: "Zero-Knowledge Architecture",
        items: ["Passwords never visible (bcrypt hashed)", "30-min session timeout", "Credential masking in APIs", "No plain-text in logs"]
      },
      {
        icon: Fingerprint,
        title: "Multi-Factor Authentication",
        items: ["TOTP-based MFA (Google Authenticator)", "Backup codes for emergencies", "Per-user enforcement options", "QR code setup"]
      },
      {
        icon: FileText,
        title: "WORM Audit Logs",
        items: ["Write-Once-Read-Many logging", "Every action timestamped", "7-year retention (HIPAA)", "One-click audit export"]
      },
      {
        icon: AlertTriangle,
        title: "PHI Detection & Redaction",
        items: ["Auto-detect SSN, DOB, MRN", "Real-time exposure warnings", "Credit card scrubbing", "Configurable sensitivity"]
      }
    ]
  },

  // ========== SLIDE 14: Telegram Bot ==========
  {
    id: "telegram",
    type: "screenshot",
    title: "Isaac Bot — Field Operations",
    subtitle: "Two-Way Updates via @askisaac_bot",
    screenshotUrl: DEMO_SCREENSHOTS.telegram,
    mockInterface: "telegram",
    annotations: [
      { x: 25, y: 40, number: 1, label: "/arrived", color: "emerald" },
      { x: 55, y: 55, number: 2, label: "/update", color: "cyan" },
      { x: 80, y: 35, number: 3, label: "Photo Upload", color: "amber" }
    ],
    benefits: [
      "Contractors: /arrived, /update, /complete",
      "Real-time hospital notifications",
      "Photo documentation in field",
      "Works on any smartphone"
    ]
  },

  // ========== SLIDE 15: Response Tiers ==========
  {
    id: "response-tiers",
    type: "tiers",
    title: "Response Guarantee Tiers",
    subtitle: "Choose Speed Based on Urgency",
    tiers: [
      {
        name: "Standard",
        time: "24-48 hrs",
        description: "Routine maintenance & inventory requests",
        color: "slate",
        icon: Clock
      },
      {
        name: "Priority",
        time: "4-8 hrs",
        description: "Urgent same-day needs",
        color: "amber",
        icon: Zap
      },
      {
        name: "STAT",
        time: "2 hrs",
        description: "OR/ER emergencies — surgery-critical",
        color: "red",
        icon: Target
      }
    ]
  },

  // ========== SLIDE 16: Partnership Tiers ==========
  {
    id: "partnership-tiers",
    type: "partnership",
    title: "Partnership Models",
    subtitle: "Flexible Engagement Based on Your Needs",
    tiers: [
      {
        tier: "Tier 1",
        name: "Managed Compliance",
        mode: "Full Service",
        description: "We handle everything — dispatch, compliance documentation, project management. You get results.",
        icon: Award,
        color: "cyan"
      },
      {
        tier: "Tier 2",
        name: "Enterprise Marketplace",
        mode: "Self-Service Platform",
        description: "Access our vetted network with competitive bidding. You run projects, we provide infrastructure.",
        icon: Layers,
        color: "emerald",
        popular: true
      },
      {
        tier: "Tier 3",
        name: "White-Label Infrastructure",
        mode: "SaaS License",
        description: "License the AskIsaac platform under your brand. Your network, your rules, our technology.",
        icon: Settings,
        color: "violet"
      }
    ]
  },

  // ========== SLIDE 17: CTA ==========
  {
    id: "cta",
    type: "cta",
    title: "Ready to Transform Your Operations?",
    subtitle: "Post a need. Get bids in minutes. Track fulfillment live.",
    options: [
      {
        type: "Healthcare Facility",
        action: "Request a Demo",
        link: "/request-demo?type=hospital",
        description: "On-demand access to supplies & services",
        icon: Building2,
        color: "blue"
      },
      {
        type: "Service Contractor",
        action: "Join Network",
        link: "/request-demo?type=contractor",
        description: "Win jobs through fair bidding",
        icon: Wrench,
        color: "emerald"
      },
      {
        type: "Medical Supplier",
        action: "Partner With Us",
        link: "/request-demo?type=supplier",
        description: "Reach hospitals when GPO fails",
        icon: Truck,
        color: "amber"
      }
    ],
    contact: {
      email: "info@sterilemate.com",
      tollFree: "800.988.3892",
      direct: "281-456-3458",
      website: "sterilemate.com"
    }
  }
];

// Sales Rep Talking Points for each slide
const SALES_NOTES = {
  "welcome": {
    headline: "Opening Hook",
    duration: "30-45 seconds",
    talkingPoints: [
      "Start with pain point: 'How much time does your team spend chasing vendors and tracking work orders?'",
      "AskIsaac eliminates the chaos — one platform for supplies, maintenance, and compliance",
      "Mention the 3 stakeholder groups: Hospitals, Contractors, Suppliers — all connected",
      "Emphasize HIPAA compliance upfront — this is healthcare-grade security"
    ],
    objectionHandlers: [
      "If they ask about pricing: 'Let me show you the value first, then we'll discuss partnership tiers at the end'",
      "If they seem skeptical: 'I understand — let me show you exactly how this works'"
    ],
    transition: "Let me show you all the services we provide under one roof..."
  },
  "services-overview": {
    headline: "Value Proposition",
    duration: "45-60 seconds",
    talkingPoints: [
      "Key message: 'One call, one platform — we handle everything'",
      "Water Purification is our specialty — AAMI ST108 compliance for Sterile Processing",
      "All contractors are pre-vetted for healthcare environments",
      "This eliminates vendor management headaches — no more juggling 10 different contacts"
    ],
    objectionHandlers: [
      "If they have existing vendors: 'We can work alongside them or replace them — your choice'",
      "If they ask about coverage area: 'We're building a nationwide network, currently strong in Texas'"
    ],
    transition: "Now let me show you what your team will see every day..."
  },
  "hospital-dashboard": {
    headline: "Hospital Experience",
    duration: "60 seconds",
    talkingPoints: [
      "This is the Hospital Command Center — everything at a glance",
      "Point out: Real-time stats, quick navigation, contextual hero banner",
      "The blue 'Add' button — one click to submit any request",
      "Departments can be filtered — Facilities vs Supply Chain see different views"
    ],
    objectionHandlers: [
      "If they worry about training: 'The interface is intuitive — most users are productive in under an hour'",
      "If they ask about mobile: 'Full mobile support — works on any device'"
    ],
    transition: "Let me show you how work orders flow through the system..."
  },
  "maintenance-pipeline": {
    headline: "Visual Workflow",
    duration: "45 seconds",
    talkingPoints: [
      "Drag-and-drop Kanban — just like Trello but built for healthcare maintenance",
      "5 stages: Intake → Triage → Dispatched → In Progress → Completed",
      "Color-coded priority: RED = High, AMBER = Medium, GREEN = Low",
      "Click any card to see full details, photos, notes, audit trail"
    ],
    objectionHandlers: [
      "If they use a CMMS already: 'We integrate with existing systems or can replace them entirely'"
    ],
    transition: "Supply chain is just as visual — let me show you backorder tracking..."
  },
  "backorder-tracking": {
    headline: "Supply Chain Pain Point",
    duration: "60 seconds",
    talkingPoints: [
      "This is where we solve the GPO backorder nightmare",
      "Real-time alerts when your GPO can't fulfill critical supplies",
      "Anonymous bidding — suppliers compete, you get best price fast",
      "Every edit tracked with full audit trail for compliance"
    ],
    objectionHandlers: [
      "If they're locked into GPO contracts: 'We're the backup plan when GPO fails — not a replacement'",
      "If they worry about rogue purchasing: 'Full approval workflows and audit trails'"
    ],
    transition: "Hospitals can also create their own competitive bids..."
  },
  "quickbids": {
    headline: "Proactive Sourcing",
    duration: "45 seconds",
    talkingPoints: [
      "QuickBids = hospital-initiated competitive bidding",
      "Not waiting for backorders — proactively source better deals",
      "Set urgency tier: STAT, Priority, or Standard",
      "Compare anonymous bids and award with one click"
    ],
    objectionHandlers: [
      "If they ask who sees bids: 'Bidding is anonymous — suppliers can't see competitors'"
    ],
    transition: "Now let me show you asset lifecycle management..."
  },
  "equipment-registry": {
    headline: "Asset Management",
    duration: "45 seconds",
    talkingPoints: [
      "Every piece of equipment gets a QR code",
      "Scan with any phone — instant access to service history",
      "Automated PM scheduling — never miss critical maintenance",
      "Warranty tracking prevents unnecessary spend"
    ],
    objectionHandlers: [
      "If they have existing asset tags: 'We can import your existing data and add QR codes'"
    ],
    transition: "For Sterile Processing, water quality is critical..."
  },
  "water-testing": {
    headline: "Compliance Differentiator",
    duration: "60 seconds",
    talkingPoints: [
      "AAMI ST108 compliance is non-negotiable for Sterile Processing",
      "We handle sampling, lab coordination, and results interpretation",
      "Real-time dashboard shows all water quality parameters",
      "One-click export for Joint Commission audits"
    ],
    objectionHandlers: [
      "If they have in-house testing: 'We augment your team and ensure nothing falls through cracks'"
    ],
    transition: "Let me show you how we verify contractor work..."
  },
  "gps-tracking": {
    headline: "Verification & Accountability",
    duration: "45 seconds",
    talkingPoints: [
      "GPS check-in proves contractors actually showed up",
      "Hospital gets instant notification when contractor arrives",
      "Time-stamped for accurate billing",
      "No more 'he said, she said' about on-site time"
    ],
    objectionHandlers: [
      "If they worry about privacy: 'Tracking only during work hours, not personal time'"
    ],
    transition: "Contractors love this because it streamlines their billing..."
  },
  "contractor-earnings": {
    headline: "Contractor Value Prop",
    duration: "30 seconds",
    talkingPoints: [
      "Contractors see real-time earnings dashboard",
      "Auto-generated invoices from tracked time",
      "STAT jobs pay premium rates — incentivizes fast response",
      "No more chasing payments"
    ],
    objectionHandlers: [],
    transition: "Suppliers have their own streamlined experience..."
  },
  "supplier-quotes": {
    headline: "Supplier Value Prop",
    duration: "45 seconds",
    talkingPoints: [
      "Instant RFQ notifications — first responder advantage",
      "Anonymous bidding = fair competition, no favoritism",
      "STAT orders command premium pricing",
      "Direct access to hospital procurement teams"
    ],
    objectionHandlers: [
      "If they ask about supplier onboarding: 'Simple registration, we verify credentials'"
    ],
    transition: "All of this is orchestrated from the Admin Command Center..."
  },
  "admin-command": {
    headline: "Platform Control",
    duration: "45 seconds",
    talkingPoints: [
      "This is where AskIsaac admins manage the entire network",
      "See all hospitals, contractors, suppliers at a glance",
      "Dispatch jobs, reassign work, monitor SLAs",
      "Revenue and performance analytics"
    ],
    objectionHandlers: [
      "If they want their own admin access: 'Available in Enterprise tier'"
    ],
    transition: "Security is paramount in healthcare..."
  },
  "security": {
    headline: "Compliance & Security",
    duration: "60 seconds",
    talkingPoints: [
      "Zero-knowledge architecture — we can't see your passwords",
      "Multi-factor authentication available for all users",
      "WORM audit logs — every action timestamped forever",
      "PHI detection warns before sensitive data is shared"
    ],
    objectionHandlers: [
      "If they ask about SOC2/HIPAA: 'We're designed for healthcare compliance from day one'",
      "If they need a BAA: 'Business Associate Agreement available for enterprise clients'"
    ],
    transition: "Your team can interact via our Telegram bot..."
  },
  "telegram": {
    headline: "Field Operations",
    duration: "45 seconds",
    talkingPoints: [
      "Contractors use Telegram for real-time updates",
      "/arrived command triggers GPS check-in and hospital notification",
      "Photo uploads for work documentation",
      "Works on any smartphone — no app download required"
    ],
    objectionHandlers: [
      "If they don't use Telegram: 'Also available via web chat and email notifications'"
    ],
    transition: "We offer guaranteed response times..."
  },
  "response-tiers": {
    headline: "Service Levels",
    duration: "45 seconds",
    talkingPoints: [
      "Three tiers based on urgency",
      "Standard: 24-48 hours for routine needs",
      "Priority: 4-8 hours for urgent issues",
      "STAT: 2 hours for OR/ER emergencies — surgery can't wait"
    ],
    objectionHandlers: [
      "If they ask about guarantees: 'SLA-backed response times with escalation protocols'"
    ],
    transition: "Let me explain our partnership models..."
  },
  "partnership-tiers": {
    headline: "Business Model",
    duration: "60 seconds",
    talkingPoints: [
      "Tier 1 - Managed Compliance: We do everything, you get results",
      "Tier 2 - Enterprise Marketplace: Self-service platform access (most popular)",
      "Tier 3 - White Label: License our tech under your brand",
      "Pricing varies by volume and service level"
    ],
    objectionHandlers: [
      "If they want pricing: 'Let's schedule a follow-up to discuss your specific needs'",
      "If they need board approval: 'I can provide an executive summary and ROI analysis'"
    ],
    transition: "Ready to get started?"
  },
  "cta": {
    headline: "Closing",
    duration: "30-60 seconds",
    talkingPoints: [
      "Ask: 'Based on what you've seen, which pain point resonates most?'",
      "Offer: 'I can set up a pilot program to prove the value'",
      "Create urgency: 'We're onboarding 3 new hospitals this quarter'",
      "Clear next step: 'Let's schedule a 30-minute deep dive with your team'"
    ],
    objectionHandlers: [
      "If they need to think: 'I'll send a summary email — when can we reconnect?'",
      "If they want references: 'Happy to connect you with current clients'"
    ],
    transition: "Thank you for your time!"
  }
};

// Mock Interface Overlay - Simulates the actual platform UI on top of screenshots
const MockInterfaceOverlay = ({ type }) => {
  const overlays = {
    "hospital-dashboard": (
      <div className="absolute inset-0 pointer-events-none">
        {/* Sidebar Mock */}
        <div className="absolute left-0 top-0 bottom-0 w-44 bg-slate-900/95 border-r border-slate-700">
          <div className="p-3">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-7 h-7 bg-cyan-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xs">AI</span>
              </div>
              <span className="text-white font-semibold text-sm">AskIsaac</span>
            </div>
            {["Dashboard", "Maintenance", "Backorders", "QuickBids", "Equipment"].map((item, idx) => (
              <div 
                key={item}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-lg mb-1 ${idx === 0 ? 'bg-blue-600 text-white' : 'text-slate-400'}`}
              >
                <div className="w-3 h-3 rounded bg-current opacity-50" />
                <span className="text-xs">{item}</span>
              </div>
            ))}
          </div>
        </div>
        {/* Stats Cards */}
        <div className="absolute left-48 right-4 top-24 flex gap-3">
          {[
            { label: "Work Orders", value: "30", color: "text-white" },
            { label: "Pending", value: "5", color: "text-amber-400" },
            { label: "In Progress", value: "4", color: "text-blue-400" },
            { label: "Completed", value: "21", color: "text-emerald-400" }
          ].map((stat) => (
            <div key={stat.label} className="flex-1 bg-white/10 backdrop-blur rounded-lg p-3">
              <p className="text-slate-400 text-[10px]">{stat.label}</p>
              <p className={`text-xl font-bold ${stat.color}`}>{stat.value}</p>
            </div>
          ))}
        </div>
      </div>
    ),
    "kanban": (
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-2 right-2 top-16 bottom-4 flex gap-2">
          {[
            { name: "Intake", count: 3, color: "bg-slate-600" },
            { name: "Triage", count: 1, color: "bg-amber-600" },
            { name: "Dispatch", count: 2, color: "bg-blue-600" },
            { name: "In Progress", count: 2, color: "bg-emerald-600" },
            { name: "Completed", count: 5, color: "bg-cyan-600" }
          ].map((col) => (
            <div key={col.name} className="flex-1 bg-slate-800/90 rounded-lg p-2">
              <div className="flex items-center justify-between mb-2">
                <div className={`px-2 py-0.5 ${col.color} rounded text-white text-[10px] font-medium`}>
                  {col.name}
                </div>
                <span className="text-slate-400 text-[10px]">{col.count}</span>
              </div>
              {Array(Math.min(col.count, 2)).fill(0).map((_, idx) => (
                <div key={idx} className="bg-slate-700/80 rounded p-2 mb-1.5">
                  <div className="flex items-center gap-1 mb-0.5">
                    <span className="px-1 py-0.5 bg-red-500 text-white text-[8px] rounded">HIGH</span>
                  </div>
                  <p className="text-white text-[10px]">WO-{1000 + idx}</p>
                  <p className="text-slate-400 text-[8px]">Building A</p>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    ),
    "backorders": (
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-4 right-4 top-20 bg-slate-800/90 rounded-lg p-3">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white text-sm font-semibold">Active Backorders</h3>
            <div className="flex gap-2">
              <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-[10px] rounded">3 GPO Alerts</span>
              <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-[10px] rounded">5 Bids Received</span>
            </div>
          </div>
          <div className="space-y-2">
            {["Surgical Gloves - Size 7", "IV Catheters 22G", "Suture Kit Premium"].map((item, idx) => (
              <div key={idx} className="flex items-center justify-between bg-slate-700/50 rounded p-2">
                <div>
                  <p className="text-white text-xs">{item}</p>
                  <p className="text-slate-400 text-[10px]">Qty: {50 + idx * 25}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 ${idx === 0 ? 'bg-red-500' : 'bg-amber-500'} text-white text-[8px] rounded`}>
                    {idx === 0 ? 'STAT' : 'Priority'}
                  </span>
                  <span className="text-emerald-400 text-[10px]">{idx + 2} bids</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    ),
    "quickbids": (
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-4 right-4 top-16 bg-slate-800/90 rounded-lg p-3">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white text-sm font-semibold">My QuickBids</h3>
            <button className="px-3 py-1 bg-emerald-600 text-white text-[10px] rounded-lg">+ Create Bid</button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { title: "Dialysis Filters", status: "Active", bids: 4 },
              { title: "OR Table Parts", status: "Awarded", bids: 6 },
              { title: "Sterilizer Gaskets", status: "Pending", bids: 0 }
            ].map((bid, idx) => (
              <div key={idx} className="bg-slate-700/50 rounded p-2">
                <p className="text-white text-xs font-medium">{bid.title}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className={`px-1.5 py-0.5 ${bid.status === 'Active' ? 'bg-blue-500' : bid.status === 'Awarded' ? 'bg-emerald-500' : 'bg-slate-500'} text-white text-[8px] rounded`}>
                    {bid.status}
                  </span>
                  <span className="text-slate-400 text-[10px]">{bid.bids} bids</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    ),
    "equipment": (
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-4 right-4 top-16 flex gap-3">
          {/* QR Scanner */}
          <div className="w-1/3 bg-slate-800/90 rounded-lg p-3 text-center">
            <QrCode className="w-16 h-16 text-cyan-400 mx-auto mb-2" />
            <p className="text-white text-xs font-medium">Scan QR Code</p>
            <p className="text-slate-400 text-[10px]">Point camera at asset</p>
          </div>
          {/* Asset Details */}
          <div className="flex-1 bg-slate-800/90 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-white text-xs font-medium">Autoclave - Steris V162</p>
                <p className="text-slate-400 text-[10px]">Asset #: SPD-AC-001</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-[10px]">
              <div className="bg-slate-700/50 rounded p-1.5">
                <p className="text-slate-400">Last Service</p>
                <p className="text-white">Jan 15, 2026</p>
              </div>
              <div className="bg-slate-700/50 rounded p-1.5">
                <p className="text-slate-400">Next PM Due</p>
                <p className="text-amber-400">Feb 15, 2026</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
    "water-testing": (
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-4 right-4 top-16 bg-slate-800/90 rounded-lg p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Droplets className="w-5 h-5 text-cyan-400" />
              <h3 className="text-white text-sm font-semibold">AAMI ST108 Testing</h3>
            </div>
            <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-[10px] rounded">Compliant</span>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {[
              { param: "Bacteria", value: "<10 CFU", status: "pass" },
              { param: "Endotoxin", value: "<0.25 EU", status: "pass" },
              { param: "TOC", value: "450 ppb", status: "pass" },
              { param: "Conductivity", value: "1.2 µS", status: "pass" }
            ].map((test) => (
              <div key={test.param} className="bg-slate-700/50 rounded p-2 text-center">
                <p className="text-slate-400 text-[10px]">{test.param}</p>
                <p className="text-white text-sm font-bold">{test.value}</p>
                <CheckCircle className="w-3 h-3 text-emerald-400 mx-auto mt-1" />
              </div>
            ))}
          </div>
        </div>
      </div>
    ),
    "gps": (
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-4 right-4 top-16 bg-slate-800/90 rounded-lg p-3">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white text-sm font-semibold">Live Contractor Tracking</h3>
            <span className="flex items-center gap-1 text-emerald-400 text-[10px]">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
              2 On-Site
            </span>
          </div>
          <div className="relative h-32 bg-slate-700/50 rounded-lg overflow-hidden">
            {/* Map placeholder */}
            <div className="absolute inset-0 bg-gradient-to-br from-slate-600/20 to-slate-800/20"></div>
            {/* Markers */}
            <div className="absolute left-1/4 top-1/3 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center animate-pulse">
              <MapPin className="w-4 h-4 text-white" />
            </div>
            <div className="absolute left-2/3 top-1/2 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
              <MapPin className="w-4 h-4 text-white" />
            </div>
            {/* Legend */}
            <div className="absolute bottom-2 right-2 bg-slate-900/80 rounded px-2 py-1 text-[8px] text-white">
              Memorial Hermann - Main
            </div>
          </div>
        </div>
      </div>
    ),
    "earnings": (
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-4 right-4 top-16 bg-slate-800/90 rounded-lg p-3">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-white text-sm font-semibold">January Earnings</h3>
              <p className="text-emerald-400 text-lg font-bold">$12,450.00</p>
            </div>
            <button className="px-3 py-1 bg-amber-600 text-white text-[10px] rounded-lg flex items-center gap-1">
              <Receipt className="w-3 h-3" /> Generate Invoice
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: "Completed Jobs", value: "18", icon: CheckCircle },
              { label: "Hours Logged", value: "124", icon: Clock },
              { label: "STAT Bonus", value: "$1,200", icon: Zap }
            ].map((stat) => (
              <div key={stat.label} className="bg-slate-700/50 rounded p-2 text-center">
                <stat.icon className="w-4 h-4 text-cyan-400 mx-auto mb-1" />
                <p className="text-white text-sm font-bold">{stat.value}</p>
                <p className="text-slate-400 text-[8px]">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    ),
    "supplier": (
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-4 right-4 top-16 bg-slate-800/90 rounded-lg p-3">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white text-sm font-semibold">Quote Requests</h3>
            <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-[10px] rounded">3 New RFQs</span>
          </div>
          <div className="space-y-2">
            {[
              { item: "Surgical Gloves Nitrile", qty: 500, tier: "STAT", time: "2 hrs" },
              { item: "IV Solution 0.9%", qty: 200, tier: "Priority", time: "4 hrs" }
            ].map((rfq, idx) => (
              <div key={idx} className="flex items-center justify-between bg-slate-700/50 rounded p-2">
                <div>
                  <p className="text-white text-xs">{rfq.item}</p>
                  <p className="text-slate-400 text-[10px]">Qty: {rfq.qty}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 ${rfq.tier === 'STAT' ? 'bg-red-500' : 'bg-amber-500'} text-white text-[8px] rounded`}>
                    {rfq.tier}
                  </span>
                  <button className="px-2 py-1 bg-emerald-600 text-white text-[8px] rounded">Submit Quote</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    ),
    "admin": (
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-44 right-4 top-4 flex gap-3">
          {[
            { label: "Hospitals", value: "12", color: "text-blue-400" },
            { label: "Contractors", value: "48", color: "text-emerald-400" },
            { label: "Suppliers", value: "23", color: "text-amber-400" },
            { label: "Open Jobs", value: "156", color: "text-white" }
          ].map((stat) => (
            <div key={stat.label} className="flex-1 bg-white/10 backdrop-blur rounded-lg p-2">
              <p className="text-slate-400 text-[10px]">{stat.label}</p>
              <p className={`text-lg font-bold ${stat.color}`}>{stat.value}</p>
            </div>
          ))}
        </div>
        {/* Mini Pipeline */}
        <div className="absolute left-44 right-4 top-24 bg-slate-800/90 rounded-lg p-2">
          <p className="text-white text-xs font-medium mb-2">Backorder Pipeline</p>
          <div className="flex gap-1">
            {["New", "Bidding", "Awarded", "Shipped", "Delivered"].map((stage, idx) => (
              <div key={stage} className="flex-1 text-center">
                <div className={`h-1.5 rounded ${idx < 2 ? 'bg-blue-500' : idx < 4 ? 'bg-emerald-500' : 'bg-cyan-500'}`}></div>
                <p className="text-[8px] text-slate-400 mt-1">{stage}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    ),
    "telegram": (
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-1/4 right-1/4 top-12 bg-slate-800/95 rounded-xl p-3 border border-slate-700">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
              <Send className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-white text-sm font-medium">@askisaac_bot</p>
              <p className="text-slate-400 text-[10px]">Online</p>
            </div>
          </div>
          <div className="space-y-2">
            <div className="bg-slate-700/50 rounded-lg p-2 max-w-[80%]">
              <p className="text-white text-[11px]">🔔 New Job Assigned: WO-1234</p>
              <p className="text-slate-400 text-[9px]">Memorial Hermann - Plumbing</p>
            </div>
            <div className="bg-blue-600 rounded-lg p-2 max-w-[60%] ml-auto">
              <p className="text-white text-[11px]">/arrived</p>
            </div>
            <div className="bg-slate-700/50 rounded-lg p-2 max-w-[80%]">
              <p className="text-emerald-400 text-[11px]">✅ Check-in confirmed!</p>
              <p className="text-slate-400 text-[9px]">Hospital notified of your arrival</p>
            </div>
          </div>
        </div>
      </div>
    )
  };
  
  return overlays[type] || null;
};

export default function EnhancedDemoPresentation({ open, onClose }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [autoPlay, setAutoPlay] = useState(false);
  const [autoPlaySpeed, setAutoPlaySpeed] = useState(5000); // 5 seconds default
  const [showNotes, setShowNotes] = useState(false);
  const slide = ENHANCED_DEMO_SLIDES[currentSlide];
  const notes = SALES_NOTES[slide.id];

  const nextSlide = () => {
    if (currentSlide < ENHANCED_DEMO_SLIDES.length - 1 && !isAnimating) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentSlide(currentSlide + 1);
        setIsAnimating(false);
      }, 300);
    } else if (currentSlide === ENHANCED_DEMO_SLIDES.length - 1 && autoPlay) {
      // Loop back to start in auto-play mode
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentSlide(0);
        setIsAnimating(false);
      }, 300);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0 && !isAnimating) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentSlide(currentSlide - 1);
        setIsAnimating(false);
      }, 300);
    }
  };

  const goToSlide = (index) => {
    if (!isAnimating) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentSlide(index);
        setIsAnimating(false);
      }, 300);
    }
  };

  // Auto-play functionality
  useEffect(() => {
    if (!autoPlay || !open) return;
    
    const timer = setInterval(() => {
      if (currentSlide < ENHANCED_DEMO_SLIDES.length - 1) {
        setCurrentSlide(prev => prev + 1);
      } else {
        setCurrentSlide(0); // Loop back
      }
    }, autoPlaySpeed);

    return () => clearInterval(timer);
  }, [autoPlay, open, autoPlaySpeed, currentSlide]);

  // Stop auto-play on user interaction
  const handleUserInteraction = () => {
    if (autoPlay) {
      setAutoPlay(false);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return;
      if (e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        handleUserInteraction();
        nextSlide();
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        handleUserInteraction();
        prevSlide();
      }
      if (e.key === "Escape") onClose();
      if (e.key === "p" || e.key === "P") {
        setAutoPlay(!autoPlay);
      }
      if (e.key === "n" || e.key === "N") {
        setShowNotes(!showNotes);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, currentSlide, isAnimating, autoPlay, showNotes]);

  const renderSlide = () => {
    switch (slide.type) {
      case "welcome":
        return (
          <div className="space-y-8 text-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/20 rounded-full mb-4">
                <Play className="w-4 h-4 text-cyan-400" />
                <span className="text-cyan-200 text-sm font-medium">17-Step Product Tour</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">{slide.title}</h2>
              <p className="text-xl text-cyan-300 font-medium mb-2">{slide.subtitle}</p>
              <p className="text-slate-300 max-w-2xl mx-auto">{slide.description}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
              {slide.highlights.map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div key={idx} className="bg-slate-800/50 rounded-xl p-4 border border-slate-700 hover:border-cyan-500/50 transition-colors">
                    <Icon className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
                    <p className="text-white text-sm font-medium">{item.text}</p>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col items-center gap-2 pt-4">
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <MousePointer className="w-4 h-4" />
                <span>Use arrow keys or click Next to navigate</span>
              </div>
              <div className="flex items-center gap-2 text-emerald-400/70 text-xs">
                <Play className="w-3 h-3" />
                <span>Press P or click Auto to start kiosk mode</span>
              </div>
            </div>
          </div>
        );

      case "services":
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-white mb-2">{slide.title}</h2>
              <p className="text-slate-300">{slide.subtitle}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {slide.services.map((service, idx) => {
                const Icon = service.icon;
                const colorMap = {
                  cyan: "border-cyan-500/30 bg-cyan-500/10",
                  blue: "border-blue-500/30 bg-blue-500/10",
                  amber: "border-amber-500/30 bg-amber-500/10",
                  emerald: "border-emerald-500/30 bg-emerald-500/10",
                  violet: "border-violet-500/30 bg-violet-500/10",
                  orange: "border-orange-500/30 bg-orange-500/10"
                };
                const iconColorMap = {
                  cyan: "text-cyan-400",
                  blue: "text-blue-400",
                  amber: "text-amber-400",
                  emerald: "text-emerald-400",
                  violet: "text-violet-400",
                  orange: "text-orange-400"
                };
                return (
                  <div key={idx} className={`rounded-xl p-4 border ${colorMap[service.color]} hover:scale-105 transition-transform`}>
                    <Icon className={`w-8 h-8 ${iconColorMap[service.color]} mb-3`} />
                    <h3 className="text-white font-semibold text-sm mb-1">{service.name}</h3>
                    <p className="text-slate-400 text-xs">{service.description}</p>
                  </div>
                );
              })}
            </div>

            <div className="text-center pt-4">
              <p className="text-slate-400 text-sm">
                <CheckCircle className="w-4 h-4 inline mr-1 text-emerald-400" />
                All services pre-vetted for healthcare compliance
              </p>
            </div>
          </div>
        );

      case "screenshot":
        return (
          <div className="space-y-4">
            <div className="text-center mb-2">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-1">{slide.title}</h2>
              <p className="text-cyan-300">{slide.subtitle}</p>
            </div>

            {/* Screenshot Container with Annotations */}
            <div className="relative rounded-xl overflow-hidden border border-slate-700 bg-slate-900">
              <div className="relative aspect-video">
                <img 
                  src={slide.screenshotUrl}
                  alt={slide.title}
                  className="w-full h-full object-cover opacity-20"
                />
                
                {/* Mock Interface Overlay */}
                {slide.mockInterface && <MockInterfaceOverlay type={slide.mockInterface} />}
                
                {/* Annotations */}
                {slide.annotations?.map((ann, idx) => (
                  <Annotation 
                    key={idx}
                    x={ann.x}
                    y={ann.y}
                    number={ann.number}
                    label={ann.label}
                    color={ann.color}
                  />
                ))}
                
                {/* Arrows */}
                {slide.arrows?.map((arrow, idx) => (
                  <AnimatedArrow 
                    key={idx}
                    x1={arrow.x1}
                    y1={arrow.y1}
                    x2={arrow.x2}
                    y2={arrow.y2}
                    color={arrow.color}
                  />
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {slide.benefits?.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 bg-slate-800/50 rounded-lg p-2">
                  <CheckCircle className="w-3 h-3 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <p className="text-slate-200 text-xs">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case "security":
        return (
          <div className="space-y-6">
            <div className="text-center mb-4">
              <h2 className="text-3xl font-bold text-white mb-2">{slide.title}</h2>
              <p className="text-slate-300">{slide.subtitle}</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {slide.features.map((feature, idx) => {
                const Icon = feature.icon;
                return (
                  <div key={idx} className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-cyan-500/20 rounded-lg">
                        <Icon className="w-5 h-5 text-cyan-400" />
                      </div>
                      <h3 className="text-white font-semibold">{feature.title}</h3>
                    </div>
                    <ul className="space-y-1.5">
                      {feature.items.map((item, iidx) => (
                        <li key={iidx} className="flex items-center gap-2 text-slate-300 text-sm">
                          <CheckCircle className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case "tiers":
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-white mb-2">{slide.title}</h2>
              <p className="text-slate-300">{slide.subtitle}</p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {slide.tiers.map((tier, idx) => {
                const Icon = tier.icon;
                const colorClasses = {
                  slate: "border-slate-500 bg-slate-500/10",
                  amber: "border-amber-500 bg-amber-500/10",
                  red: "border-red-500 bg-red-500/10"
                };
                const textColors = {
                  slate: "text-slate-300",
                  amber: "text-amber-300",
                  red: "text-red-300"
                };
                return (
                  <div 
                    key={idx}
                    className={`rounded-xl p-6 border-2 ${colorClasses[tier.color]} text-center`}
                  >
                    <Icon className={`w-12 h-12 ${textColors[tier.color]} mx-auto mb-4`} />
                    <h3 className="text-white text-xl font-bold mb-1">{tier.name}</h3>
                    <p className={`text-4xl font-bold ${textColors[tier.color]} mb-2`}>{tier.time}</p>
                    <p className="text-slate-400 text-sm">{tier.description}</p>
                  </div>
                );
              })}
            </div>

            <div className="text-center pt-4">
              <p className="text-slate-400 text-sm">
                <Shield className="w-4 h-4 inline mr-1" />
                All tiers include GPS verification, audit logging, and compliance documentation
              </p>
            </div>
          </div>
        );

      case "partnership":
        return (
          <div className="space-y-6">
            <div className="text-center mb-4">
              <h2 className="text-3xl font-bold text-white mb-2">{slide.title}</h2>
              <p className="text-slate-300">{slide.subtitle}</p>
            </div>

            <div className="grid md:grid-cols-3 gap-4 max-w-5xl mx-auto">
              {slide.tiers.map((tier, idx) => {
                const Icon = tier.icon;
                const colorMap = {
                  cyan: { bg: "bg-cyan-500/10", border: "border-cyan-500/30", text: "text-cyan-300", badge: "bg-cyan-600" },
                  emerald: { bg: "bg-emerald-500/10", border: "border-emerald-500/30", text: "text-emerald-300", badge: "bg-emerald-600" },
                  violet: { bg: "bg-violet-500/10", border: "border-violet-500/30", text: "text-violet-300", badge: "bg-violet-600" }
                };
                const colors = colorMap[tier.color];
                return (
                  <div 
                    key={idx}
                    className={`relative rounded-xl p-5 ${colors.bg} border ${tier.popular ? 'border-emerald-400 scale-105 z-10' : colors.border} transition-all`}
                  >
                    {tier.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-emerald-500 text-white text-xs font-semibold rounded-full">
                        Most Popular
                      </div>
                    )}
                    <div className="flex items-center gap-3 mb-3 mt-2">
                      <div className={`p-2 ${colors.bg} rounded-lg`}>
                        <Icon className={`w-6 h-6 ${colors.text}`} />
                      </div>
                      <div>
                        <span className={`text-xs ${colors.badge} text-white px-2 py-0.5 rounded`}>{tier.tier}</span>
                        <h3 className="text-white font-bold">{tier.name}</h3>
                      </div>
                    </div>
                    <p className={`${colors.text} text-sm font-medium mb-2`}>{tier.mode}</p>
                    <p className="text-slate-400 text-sm">{tier.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case "cta":
        return (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-white mb-2">{slide.title}</h2>
              <p className="text-slate-300">{slide.subtitle}</p>
            </div>

            <div className="grid md:grid-cols-3 gap-4 max-w-4xl mx-auto">
              {slide.options.map((option, idx) => {
                const Icon = option.icon;
                const colorClasses = {
                  blue: "hover:border-blue-500/50",
                  emerald: "hover:border-emerald-500/50",
                  amber: "hover:border-amber-500/50"
                };
                const iconColors = {
                  blue: "text-blue-400",
                  emerald: "text-emerald-400",
                  amber: "text-amber-400"
                };
                return (
                  <Link 
                    key={idx} 
                    to={option.link}
                    onClick={onClose}
                    className={`group bg-slate-800 rounded-xl p-6 border border-slate-700 ${colorClasses[option.color]} transition-all text-center`}
                  >
                    <div className="inline-flex p-3 bg-slate-700 rounded-xl mb-4 group-hover:scale-110 transition-transform">
                      <Icon className={`w-8 h-8 ${iconColors[option.color]}`} />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-1">{option.type}</h3>
                    <p className="text-slate-400 text-sm mb-4">{option.description}</p>
                    <span className={`inline-flex items-center gap-2 ${iconColors[option.color]} font-medium text-sm`}>
                      {option.action}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                );
              })}
            </div>

            {/* Contact Info */}
            <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-300 pt-4">
              <a href={`mailto:${slide.contact.email}`} className="flex items-center gap-2 hover:text-white transition-colors">
                <Mail className="w-4 h-4" />
                {slide.contact.email}
              </a>
              <span className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                {slide.contact.tollFree}
              </span>
              <span className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {slide.contact.website}
              </span>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent 
        className="dark-dialog max-w-6xl h-[90vh] border-slate-700 p-0 overflow-hidden [&>button]:text-white"
        data-dark-mode="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg">
              <Eye className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Complete Platform Tour</h3>
              <p className="text-slate-400 text-sm">Step {currentSlide + 1} of {ENHANCED_DEMO_SLIDES.length}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Sales Notes Toggle */}
            <button
              onClick={() => setShowNotes(!showNotes)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-all ${
                showNotes 
                  ? 'bg-amber-600 border-amber-500 text-white' 
                  : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700'
              }`}
              title={showNotes ? "Hide sales notes (N)" : "Show sales notes (N)"}
            >
              <FileText className="w-4 h-4" />
              <span className="text-xs font-medium hidden sm:inline">Notes</span>
            </button>
            
            {/* Auto-Play Controls */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 rounded-lg border border-slate-700">
              <button
                onClick={() => setAutoPlay(!autoPlay)}
                className={`flex items-center gap-1.5 px-2 py-1 rounded transition-all ${
                  autoPlay 
                    ? 'bg-emerald-600 text-white' 
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
                title={autoPlay ? "Pause auto-play (P)" : "Start auto-play (P)"}
              >
                {autoPlay ? (
                  <>
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                      <rect x="6" y="4" width="4" height="16" />
                      <rect x="14" y="4" width="4" height="16" />
                    </svg>
                    <span className="text-xs font-medium">Pause</span>
                  </>
                ) : (
                  <>
                    <Play className="w-3 h-3" />
                    <span className="text-xs font-medium">Auto</span>
                  </>
                )}
              </button>
              {autoPlay && (
                <select
                  value={autoPlaySpeed}
                  onChange={(e) => setAutoPlaySpeed(Number(e.target.value))}
                  className="bg-slate-700 text-slate-300 text-xs rounded px-1 py-0.5 border-none outline-none cursor-pointer"
                >
                  <option value={3000}>3s</option>
                  <option value={5000}>5s</option>
                  <option value={8000}>8s</option>
                  <option value={10000}>10s</option>
                </select>
              )}
            </div>
            
            <ShareButtons 
              title="AskIsaac - Healthcare Operations Platform"
              description="See how hospitals, contractors, and suppliers connect on one intelligent platform!"
              url="https://sterilemate.com"
              variant="dark"
              size="sm"
            />
            <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-white hover:bg-slate-800">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Video Banner (only on welcome slide) */}
        {slide.type === "welcome" && (
          <div className="relative w-full h-44 md:h-52 bg-gradient-to-b from-slate-800/50 to-transparent overflow-hidden">
            <div className="absolute inset-0 flex items-center justify-center">
              <video 
                autoPlay 
                loop 
                muted 
                playsInline
                preload="auto"
                poster={DIGITAL_ISAAC}
                className="h-full w-auto object-contain opacity-90"
                style={{ 
                  filter: 'drop-shadow(0 0 40px rgba(96, 165, 250, 0.3))',
                  maxWidth: '100%'
                }}
              >
                <source src={DIGITAL_ISAAC_VIDEO} type="video/mp4" />
              </video>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-slate-900 to-transparent" />
          </div>
        )}

        {/* Slide Content */}
        <div className={`flex-1 overflow-y-auto p-6 md:p-8 transition-opacity duration-300 ${isAnimating ? 'opacity-0' : 'opacity-100'}`}>
          {renderSlide()}
        </div>

        {/* Sales Notes Panel */}
        {showNotes && notes && (
          <div className="border-t border-amber-500/30 bg-amber-950/30 px-6 py-4 max-h-64 overflow-y-auto">
            <div className="flex items-start gap-6">
              {/* Left Column: Main talking points */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <div className="px-2 py-0.5 bg-amber-600 text-white text-xs font-bold rounded">
                    {notes.headline}
                  </div>
                  <span className="text-amber-300/60 text-xs">~{notes.duration}</span>
                </div>
                
                <div className="space-y-2">
                  {notes.talkingPoints.map((point, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <span className="text-amber-400 font-bold text-xs mt-0.5">{idx + 1}.</span>
                      <p className="text-amber-100/90 text-sm leading-relaxed">{point}</p>
                    </div>
                  ))}
                </div>
                
                {/* Transition */}
                <div className="mt-3 pt-2 border-t border-amber-500/20">
                  <p className="text-amber-300 text-xs italic flex items-center gap-1">
                    <ArrowRight className="w-3 h-3" />
                    {notes.transition}
                  </p>
                </div>
              </div>
              
              {/* Right Column: Objection handlers */}
              {notes.objectionHandlers && notes.objectionHandlers.length > 0 && (
                <div className="w-72 flex-shrink-0">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Shield className="w-3 h-3 text-amber-400" />
                    <span className="text-amber-400 text-xs font-semibold uppercase tracking-wide">Objection Handlers</span>
                  </div>
                  <div className="space-y-2">
                    {notes.objectionHandlers.map((handler, idx) => (
                      <div key={idx} className="bg-amber-900/30 rounded p-2 border border-amber-500/20">
                        <p className="text-amber-200/80 text-xs leading-relaxed">{handler}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer Navigation */}
        <div className="px-6 py-3 border-t border-slate-800 bg-slate-900/50">
          {/* Auto-play progress bar */}
          {autoPlay && (
            <div className="mb-2 h-0.5 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-emerald-500 transition-all ease-linear"
                style={{ 
                  width: '100%',
                  animation: `shrink ${autoPlaySpeed}ms linear infinite`
                }}
              />
              <style>{`
                @keyframes shrink {
                  from { width: 100%; }
                  to { width: 0%; }
                }
              `}</style>
            </div>
          )}
          
          <div className="flex items-center justify-between">
            {/* Progress Bar */}
            <div className="flex items-center gap-1 flex-1 mr-4">
              {ENHANCED_DEMO_SLIDES.map((s, idx) => (
                <button
                  key={s.id}
                  onClick={() => {
                    handleUserInteraction();
                    goToSlide(idx);
                  }}
                  className={`h-1.5 rounded-full transition-all flex-1 max-w-3 ${
                    idx === currentSlide 
                      ? 'bg-cyan-500 max-w-6' 
                      : idx < currentSlide
                        ? 'bg-cyan-500/50'
                        : 'bg-slate-600 hover:bg-slate-500'
                  }`}
                  title={s.title}
                />
              ))}
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  handleUserInteraction();
                  prevSlide();
                }}
                disabled={currentSlide === 0}
                className="border-slate-700 text-slate-300 hover:text-white hover:bg-slate-800 disabled:opacity-50"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              
              {currentSlide < ENHANCED_DEMO_SLIDES.length - 1 ? (
                <Button
                  size="sm"
                  onClick={() => {
                    handleUserInteraction();
                    nextSlide();
                  }}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Link to="/request-demo" onClick={onClose}>
                  <Button
                    size="sm"
                    className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white"
                  >
                    Get Started
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
